import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import useThemeStyles from '../../../hooks/useThemeStyles';
import WordCountPill from '../../../components/WordCountPill';

const ChapterCardHeader = ({
  chapterName,
  chapterNumber,
  description,
  wordCount,
  onEdit,
  disableEdit = false,
}) => {
  const { colors, getColor, isDark } = useThemeStyles();
  const hasDescription = useMemo(() => Boolean(description && description.trim()), [description]);

  const handleEditClick = (event) => {
    event.stopPropagation();
    if (onEdit) {
      onEdit();
    }
  };

  return (
    <Box
      sx={{
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: { xs: 3, md: 4 },
        px: 2,
        gap: hasDescription ? 1.5 : 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: colors.text.secondary,
            fontFamily: 'serif',
          }}
        >
          Chapter {chapterNumber}
        </Typography>
        <WordCountPill wordCount={wordCount} sx={{ height: 18, fontSize: '0.675rem' }} />
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
        }}
      >
        <Typography
          variant="h3"
          component="h2"
          sx={{
            fontFamily: 'serif',
            fontWeight: 'bold',
            color: isDark ? getColor('paper', 100) : colors.text.primary,
            lineHeight: 1.2,
            textShadow: colors.paper?.textShadow || 'none',
            wordBreak: 'break-word',
            fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem', lg: '3rem' },
          }}
        >
          {chapterName || 'Untitled Chapter'}
        </Typography>
        {!disableEdit && (
          <IconButton
            aria-label="Edit chapter"
            size="small"
            onClick={handleEditClick}
            sx={{
              color: colors.text.secondary,
              transition: 'color 0.2s ease, transform 0.2s ease',
              '&:hover': {
                color: colors.primary.main,
                transform: 'scale(1.05)',
              },
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      {hasDescription && (
        <Typography
          variant="subtitle1"
          sx={{
            fontFamily: 'serif',
            fontSize: { xs: '0.95rem', sm: '1.05rem' },
            color: colors.text.secondary,
            px: { xs: 1, sm: 2 },
            maxWidth: '40rem',
            textAlign: 'center',
            wordBreak: 'break-word',
            lineHeight: 1.45,
          }}
        >
          {description.trim()}
        </Typography>
      )}
    </Box>
  );
};

ChapterCardHeader.propTypes = {
  chapterName: PropTypes.string,
  chapterNumber: PropTypes.number.isRequired,
  description: PropTypes.string,
  wordCount: PropTypes.number,
  onEdit: PropTypes.func,
  disableEdit: PropTypes.bool,
};

ChapterCardHeader.defaultProps = {
  chapterName: '',
  description: '',
  wordCount: undefined,
  onEdit: null,
  disableEdit: false,
};

export default ChapterCardHeader;
